/* eslint-disable class-methods-use-this */
/* eslint-disable @angular-eslint/no-output-native */
/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NumberMask, TextMask } from '../services/form-controls.service';

@Component({
  standalone: false,
  selector: 'lib-cm-input',
  templateUrl: './cm-input.component.html',
  styleUrls: ['./cm-input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CmInputComponent),
      multi: true,
    },
  ],
})
export class CmInputComponent implements ControlValueAccessor {
  val: string = '';

  isCurrencyMask: boolean = false;

  onChange: (val: any) => void = () => {};

  onTouched: (val: any) => void = () => {};

  @Input() disabled: boolean = false;

  @Input() type: string = 'text';

  @Input() clearable: boolean = false;

  @Input() highlightIfSet: boolean = false;

  @Input() placeholder = 'Enter Your Value';

  @Input() formControl: FormControl = new FormControl();

  @Input() maxlength: number = 524288;

  @Input() restrictPattern = false;

  isSsnMask: boolean = false;

  // ngx-mask properties
  ngxMask: string = '';

  ngxPrefix: string = '';

  ngxSuffix: string = '';

  ngxThousandSeparator: string = ',';

  ngxDecimalMarker: '.' | ',' = '.';

  ngxDropSpecialCharacters: boolean = false;

  ngxAllowNegativeNumbers: boolean = false;

  ngxPatterns: Record<string, { pattern: RegExp; optional?: boolean }> = {};

  get hasCustomPatterns(): boolean {
    return Object.keys(this.ngxPatterns).length > 0;
  }

  @ViewChild('inputFef', { static: true }) inputElement!: ElementRef;

  @Output() blur = new EventEmitter();

  @Output() focus = new EventEmitter();

  @Input() set value(val: any) {
    if (val !== undefined && val !== null) {
      if (this.val !== val) {
        this.val = `${val}`;
        const rawVal = this.isCurrencyMask ? this.val.replace(/([$,%])/g, '') : this.val;
        if (this.onChange) {
          this.onChange(rawVal);
        }
        if (this.onTouched) {
          this.onTouched(rawVal);
        }
      }
    } else {
      this.val = '';
    }
  }

  get value() {
    return this.val;
  }

  @Input() set inputMask(value: TextMask) {
    this.isCurrencyMask = false;
    this.isSsnMask = false;
    this.resetNumberConfig();
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      this.ngxMask = '';
      return;
    }
    if (value.isSsnMask) {
      this.isSsnMask = true;
      this.ngxMask = '';
      return;
    }
    const len = value.maxLength || 60;
    const regex = value.regex || '[\\s\\S]';
    if (regex === '[0-9]' || regex === '\\d') {
      this.ngxMask = '0'.repeat(len);
    } else if (regex === '[a-zA-Z]') {
      // ngx-mask v19: no built-in letters-only token, use custom pattern
      this.ngxPatterns = { ...this.ngxPatterns, L: { pattern: /[a-zA-Z]/ } };
      this.ngxMask = 'L'.repeat(len);
    } else if (regex === '[a-zA-Z0-9]' || regex === '[A-Za-z0-9]') {
      this.ngxMask = 'A'.repeat(len); // ngx-mask v19: A=[a-zA-Z0-9], S=[a-zA-Z only]
    } else if (regex === '[\\s\\S]' || regex === '[\\S\\s]') {
      this.ngxMask = '';
    } else {
      this.ngxPatterns = { ...this.ngxPatterns, X: { pattern: new RegExp(regex) } };
      this.ngxMask = 'X'.repeat(len);
    }
    this.ngxDropSpecialCharacters = true;
  }

  @Input() set maskArray(value: any[]) {
    this.isCurrencyMask = false;
    this.isSsnMask = false;
    this.resetNumberConfig();
    if (!value || !value.length) {
      this.ngxMask = '';
      return;
    }
    if (value.length === 11 && value[3] === '-' && value[6] === '-') {
      this.isSsnMask = true;
      this.ngxMask = '';
      return;
    }
    this.ngxMask = this.convertRegexArrayToNgxMask(value);
    this.ngxDropSpecialCharacters = false;
  }

  @Input() set currencyMask(value: NumberMask | boolean) {
    const config: NumberMask =
      typeof value === 'boolean'
        ? { prefix: '$', allowDecimal: true, decimalLimit: 2, decimalSymbol: '.', includeThousandsSeparator: true }
        : value;
    this.applyNumberMask(config);
  }

  @Input() set otherMask(config: NumberMask | null) {
    if (!config) {
      this.isCurrencyMask = false;
      this.resetNumberConfig();
      return;
    }
    this.applyNumberMask(config);
  }

  onfocus(event: any) {
    this.focus.emit(event);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.isSsnMask && typeof value === 'string' && value.length) {
      const digits = value.replace(/\D/g, '');
      if (digits.length === 4) {
        this.val = `___-__-${digits}`;
        return;
      }
      if (digits.length === 9) {
        this.val = `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
        return;
      }
    }
    this.value = value;
  }

  reset() {
    if (!this.disabled) {
      this.writeValue('');
      this.inputElement.nativeElement.focus();
    }
  }

  private applyNumberMask(config: NumberMask) {
    this.isCurrencyMask = true;
    const decimalLimit = config.decimalLimit ?? 2;
    const hasDecimal = config.allowDecimal !== false;
    this.ngxMask = hasDecimal ? `separator.${decimalLimit}` : 'separator.0';
    this.ngxPrefix = config.prefix || '';
    this.ngxSuffix = config.suffix || '';
    this.ngxThousandSeparator =
      config.includeThousandsSeparator !== false ? config.thousandsSeparatorSymbol || ',' : '';
    this.ngxDecimalMarker = config.decimalSymbol === ',' ? ',' : '.';
    this.ngxAllowNegativeNumbers = !!config.allowNegative;
    this.ngxDropSpecialCharacters = false;
  }

  private resetNumberConfig() {
    this.ngxPrefix = '';
    this.ngxSuffix = '';
    this.ngxThousandSeparator = ',';
    this.ngxDecimalMarker = '.';
    this.ngxAllowNegativeNumbers = false;
  }

  private convertRegexArrayToNgxMask(arr: any[]): string {
    return arr
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item instanceof RegExp) {
          const src = item.source;
          if (src === '\\d' || src === '[0-9]') return '0';
          if (src === '[1-9]') return '9';
          if (src === '[a-zA-Z]' || src === '[a-z-A-Z]') {
            this.ngxPatterns = { ...this.ngxPatterns, L: { pattern: /[a-zA-Z]/ } };
            return 'L'; // ngx-mask v19: no built-in letters-only token
          }
          if (src === '[a-zA-Z0-9]' || src === '[A-Za-z0-9]') return 'A'; // ngx-mask v19: A=[a-zA-Z0-9]
          if (src === '[\\d_]') return '0';
          // default: create custom pattern for unrecognized regex
          this.ngxPatterns = { ...this.ngxPatterns, X: { pattern: new RegExp(src) } };
          return 'X';
        }
        return '0';
      })
      .join('');
  }
}
